import { useEffect, useState, type FormEvent } from "react";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Eye, EyeOff, LockKeyhole, UserRound, Mail } from "lucide-react";
import { ApiError, IS_DEMO } from "./api";
import { Modal } from "./components";

export interface Member {
  id: string;
  identifier: string;
  username?: string;
  email?: string;
  creator?: {
    id: string;
    nickname: string;
    type: string;
    status: string;
    joinedAt: string;
  } | null;
  nickname: string;
  bio: string;
  memberType: string;
  diamonds: number;
}
interface MemberConfig {
  settings: {
    registerEnabled: boolean;
    phoneEnabled: boolean;
    emailEnabled: boolean;
    verificationMode: string;
    testCode?: string;
  };
}
let nativeToken = "";
async function memberRequest(
  path: string,
  method = "GET",
  body?: unknown,
): Promise<any> {
  const native = Capacitor.isNativePlatform();
  const base = native
    ? import.meta.env.VITE_NATIVE_API_BASE_URL ||
      "https://ytapp.bbccys.com/api/v1"
    : import.meta.env.VITE_API_BASE_URL || "/api/v1";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(nativeToken && native
      ? { Authorization: `Bearer ${nativeToken}` }
      : {}),
  };
  try {
    let status: number, data: any;
    if (native) {
      const response = await CapacitorHttp.request({
        url: base.replace(/\/$/, "") + "/members" + path,
        method,
        headers,
        ...(body ? { data: body } : {}),
        connectTimeout: 12000,
        readTimeout: 12000,
      });
      status = response.status;
      data =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;
    } else {
      const response = await fetch(
        base.replace(/\/$/, "") + "/members" + path,
        {
          method,
          headers,
          credentials: "include",
          ...(body ? { body: JSON.stringify(body) } : {}),
          signal: AbortSignal.timeout(12000),
        },
      );
      status = response.status;
      data = await response.json();
    }
    if (status < 200 || status >= 300)
      throw new ApiError(data?.error || "账号服务暂不可用", status);
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError("无法连接账号服务，请稍后重试");
  }
}
export function useMember() {
  const [user, setUser] = useState<Member | null>(null);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [error, setError] = useState("");
  useEffect(() => {
    if (IS_DEMO) return;
    let active = true;
    memberRequest("/me")
      .then((data) => {
        if (active) setUser(data.user);
      })
      .catch((error) => {
        if (active && (!(error instanceof ApiError) || error.status !== 401))
          setError(error.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  return {
    user,
    loading,
    error,
    async signIn(mode: "login" | "register", body: Record<string, string>) {
      const data = await memberRequest("/" + mode, "POST", body);
      if (!data.user?.id) throw new ApiError("账号数据格式异常");
      // Browser uses only HttpOnly cookies. Native bearer stays in memory, never localStorage.
      if (Capacitor.isNativePlatform()) nativeToken = data.token || "";
      setUser(data.user);
      setError("");
      return data.user as Member;
    },
    async updateProfile(nickname: string, bio: string) {
      const data = await memberRequest("/me", "PUT", { nickname, bio });
      setUser(data.user);
      return data.user as Member;
    },
    async becomeCreator() {
      const data = await memberRequest("/creator", "POST", {});
      if (!data.user?.id || !data.user.creator?.id)
        throw new ApiError("创作者数据格式异常，请稍后重试");
      setUser(data.user);
      setError("");
      return data.user as Member;
    },
    async signOut() {
      await memberRequest("/logout", "POST", {});
      nativeToken = "";
      setUser(null);
      setError("");
    },
  };
}
export function AuthDialog({
  onClose,
  onSuccess,
  signIn,
}: {
  onClose: () => void;
  onSuccess: (user: Member) => void;
  signIn: ReturnType<typeof useMember>["signIn"];
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [config, setConfig] = useState<MemberConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const loadConfig = () =>
    memberRequest("/public-config")
      .then(setConfig)
      .catch((error) => setConfigError(error.message));
  useEffect(() => {
    void loadConfig();
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      onSuccess(
        await signIn(
          mode,
          mode === "register"
            ? {
                username,
                password,
                email,
                inviteCode: inviteCode.trim(),
                source: Capacitor.isNativePlatform()
                  ? "APP注册页"
                  : "新H5注册页",
              }
            : { identifier, password },
        ),
      );
    } catch (error) {
      setError(error instanceof Error ? error.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={mode === "login" ? "登录樱桃视频" : "创建账号"}
      onClose={busy ? () => {} : onClose}
    >
      <div className="auth-brand">
        <span>CHERRY</span>
        {mode === "login" && (
          <>
            <h2>每一帧，都值得遇见</h2>
            <p>登录后管理你的账号与个人资料</p>
          </>
        )}
      </div>
      <div className="auth-tabs">
        <button
          type="button"
          disabled={busy}
          className={mode === "login" ? "selected" : ""}
          onClick={() => {
            setMode("login");
            setError("");
          }}
        >
          登录
        </button>
        <button
          type="button"
          disabled={busy || !config?.settings.registerEnabled}
          className={mode === "register" ? "selected" : ""}
          onClick={() => {
            setMode("register");
            setError("");
          }}
        >
          注册
        </button>
      </div>
      {configError && (
        <p className="auth-error">
          {configError}{" "}
          <button
            onClick={() => {
              setConfigError("");
              void loadConfig();
            }}
          >
            重试
          </button>
        </p>
      )}
      <form className="edit-profile auth-form" onSubmit={submit}>
        <label>
          {mode === "register" ? "用户名" : "用户名 / 手机号 / 邮箱"}
          <div className="auth-input">
            <UserRound size={18} />
            <input
              aria-label={mode === "register" ? "注册用户名" : "登录账号"}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              minLength={mode === "register" ? 2 : 1}
              maxLength={mode === "register" ? 32 : 254}
              placeholder={
                mode === "register" ? "设置用户名" : "输入用户名、手机号或邮箱"
              }
              value={mode === "register" ? username : identifier}
              onChange={(e) =>
                mode === "register"
                  ? setUsername(e.target.value)
                  : setIdentifier(e.target.value)
              }
            />
          </div>
        </label>
        <label>
          密码
          <div className="auth-input">
            <LockKeyhole size={18} />
            <input
              aria-label={mode === "register" ? "注册密码" : "登录密码"}
              type={visible ? "text" : "password"}
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              required
              minLength={mode === "register" ? 8 : 1}
              maxLength={128}
              placeholder={
                mode === "register" ? "设置至少8位密码" : "输入登录密码"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              aria-label={visible ? "隐藏密码" : "显示密码"}
              type="button"
              onClick={() => setVisible(!visible)}
            >
              {visible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        {mode === "register" && (
          <>
            <label>
              邮箱
              <div className="auth-input">
                <Mail size={18} />
                <input
                  aria-label="注册邮箱"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  maxLength={254}
                  placeholder="输入邮箱，推荐 QQ 邮箱"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </label>
            <p className="auth-note">推荐使用 QQ 邮箱，例如 123456@qq.com</p>
            <label>
              邀请码（选填）
              <input
                aria-label="邀请码"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={64}
                placeholder="有邀请码可填写，没有可留空"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
              />
            </label>
          </>
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="primary full-width"
          type="submit"
          disabled={
            busy || (mode === "register" && !config?.settings.registerEnabled)
          }
        >
          {busy ? "正在提交…" : mode === "login" ? "登录" : "注册并登录"}
        </button>
        <p className="auth-note">
          {mode === "login"
            ? "支持已有的普通用户账号"
            : "注册后可使用用户名或邮箱登录"}
        </p>
      </form>
    </Modal>
  );
}
